import { ErrorMessage, Field, FieldArray, Form, Formik, getIn } from "formik";
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import "react-quill/dist/quill.snow.css";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useShallow } from "zustand/react/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import Spinner from "../../components/misc/Spinner";
import { DEV_MODE } from "../../constants";
import { useDeliveryNoteTemplatesStore } from "../../stores/delivery_note_templates";
import { DELIVERY_NOTE_STATE, useDeliveryNotesStore } from "../../stores/delivery_notes";

import "../../scss/signature-pad.scss";
import _ from "lodash";
import PropTypes from "prop-types";
import CustomerLookupCard from "../../components/cards/CustomerLookupCard";
import SignaturePadCard from "../../components/cards/SignaturePadCard";
import AudioRecordButton from "../../components/form/AudioRecordButton";
import ImageThumbnailGallery from "../../components/ImageThumbnailGallery";
import Search from "../../components/form/Search";
import ConfirmDialog from "../../components/modal/ConfirmDialog";
import AttachmentUploader from "../../components/uploads/AttachmentUploader";
import useOnlineStatus from "../../composables/useOnlineStatus";
import { isNetworkError, showApiError } from "../../utils/apiError";
import { getDeliveryNoteItems } from "../../services/delivery_notes";
import {
  getCustomerAddress,
  getCustomerProperties,
  getPropertyAddress,
  getPropertyCommunications,
  getPropertyContacts,
  searchCustomerByNumber,
} from "../../services/search";
import { getTechnicians } from "../../services/users";
import { Role, useAuthStore } from "../../stores/auth";
import { ITEM_CHARACTERISTIC } from "../../stores/items";
import { useNatravStore } from "../../stores/natrav";
import { useSettingsStore } from "../../stores/settings";
import { useToastStore } from "../../stores/toast";
import { useWorkOrdersStore } from "../../stores/work_orders";
import MobileActionBarContent from "../../components/mobile-action-bar/MobileActionBarContent";
import DeliveryNoteCommentsForm from "./DeliveryNoteCommentsForm";
import DeliveryNoteDocumentationForm from "./DeliveryNoteDocumentationForm";
import {
  buildDocumentationPayload,
  formatAmount,
  seedDocumentation,
} from "./utils";

const TABS = Object.freeze({
  NOTE: "note",
  DOCUMENTATION: "documentation",
  COMMENTS: "comments",
  COWORKERS: "coworkers",
});
const VALID_TABS = Object.values(TABS);

const HTTP_CONFLICT = 409;

const SAVE_ERROR = Object.freeze({
  OFFLINE: "offline",
  CONFLICT: "conflict",
});
const INITIAL_UPLOAD_STATUS = { isBusy: false, hasErrors: false };

const createInitialValues = (me, template, deliveryNote, isAddMode, autoItems = [], t, lastNotes = 20, workOrder = null) => {
  const version = isAddMode ? 0 : (deliveryNote?.version ?? 0);
  // In add mode the customer and property can be seeded from a work order;
  // a copied note (copyFromId) takes precedence over the work order.
  const customerSource = deliveryNote?.customer ?? (isAddMode ? workOrder?.customer : null);
  const propertySource =
    deliveryNote?.customer_property ?? (isAddMode ? workOrder?.customer_property : null);
  const customer = {
    ...customerSource,
    addresses: _.isEmpty(customerSource?.addresses) ? [{ street: "" }] : customerSource.addresses,
    id: isAddMode ? null : deliveryNote?.customer?.id,
  };
  const customerProperty = {
    ...propertySource,
    addresses: _.isEmpty(propertySource?.addresses)
      ? [{ street: "" }]
      : propertySource.addresses,
    id: isAddMode ? null : deliveryNote?.customer_property?.id,
  };

  const comments =
    deliveryNote?.comments.map((c) => ({
      ...c,
      id: isAddMode ? null : c.id,
    })) || [];
  comments.push({ text: "", id: null }); // allways add empty comment

  const { documentation_schema, documentation } = seedDocumentation(
    template,
    deliveryNote,
    { isAddMode },
  );

  // Collect all unique group IDs from template in order
  const groupIdsOrder = [...new Set(template.items.map((i) => i.groups?.[0]?.id ?? "custom"))];
  // Always include custom group
  if (!groupIdsOrder.includes("custom")) {
    groupIdsOrder.push("custom");
  }

  // Build template groups map
  const templateGroupsMap = new Map();
  template.items.forEach((item) => {
    const groupId = item.groups?.[0]?.id ?? "custom";
    const group = item.groups?.[0] ?? {
      name: t("delivery_notes.custom_group"),
      id: null,
    };
    if (!templateGroupsMap.has(groupId)) {
      templateGroupsMap.set(groupId, {
        ...group,
        id: groupId === "custom" ? null : groupId,
        items: [],
      });
    }
    templateGroupsMap.get(groupId).items.push(item);
  });

  // Add custom group if missing
  if (!templateGroupsMap.has("custom")) {
    templateGroupsMap.set("custom", { name: "Eigene", id: null, items: [] });
  }

  let item_groups = Array.from(templateGroupsMap.values());
  const templateOriginIds = new Set(template.items.map((i) => i.id));

  // Populate items with delivery note data or defaults
  item_groups = item_groups.map((group) => {
    const groupItems = [];
    group.items.forEach((subItem) => {
      let visibleItems = [];
      if (deliveryNote) {
        visibleItems = deliveryNote.items
          .filter((dnItem) => dnItem.origin !== null && _.eq(dnItem.number, subItem.number))
          .map((dnItem, dnItemIndex) => ({
            ...subItem, // this is our overly template
            // only in edit mode we keep original item id
            id: isAddMode ? null : dnItem.id, //
            origin_id: dnItem.origin.id,
            amount: formatAmount(dnItem.amount),
            attributes: dnItem.attributes.map((dnAttr) => ({
              ...dnAttr,
              id: isAddMode ? null : dnAttr.id,
              origin_id: dnAttr.origin.id,
              isNewChoice: false,
            })),
            highlightBg: true,
            isAdditionalItem: dnItemIndex > 0,
          }));
      }
      if (visibleItems.length === 0) {
        visibleItems.push({
          ...subItem,
          id: null,
          origin_id: subItem.id,
          amount: formatAmount(subItem?.default_amount || ""),
          attributes: subItem.attributes.map((subAttr) => ({
            ...subAttr,
            id: null,
            origin_id: subAttr.id,
            isNewChoice: false,
          })),
          highlightBg: !_.isNull(subItem?.default_amount),
          isAdditionalItem: false,
        });
      }
      groupItems.push(...visibleItems);
    });

    // Handle custom group
    if (group.id === null) {
      const customDnItems = deliveryNote?.items?.filter((dni) => _.isNull(dni.origin)) ?? [];
      const customFormItems = customDnItems.map((dni) => ({
        id: isAddMode ? null : dni.id,
        number: dni.number,
        name: dni.name,
        description: "",
        amount: formatAmount(dni.amount),
        origin_id: null,
        attributes: [],
        highlightBg: true,
        isAdditionalItem: false,
      }));
      groupItems.push(...customFormItems);
      // Add empty item for new custom
      groupItems.push({
        id: null,
        number: "",
        name: "",
        description: "",
        amount: "",
        origin_id: null,
        attributes: [],
        highlightBg: false,
        isAdditionalItem: false,
      });
    }

    return {
      ...group,
      items: groupItems,
    };
  });

  // Preserve delivery note items whose origin was removed from the template
  if (deliveryNote) {
    const orphanedDnItems = deliveryNote.items.filter(
      (dni) => dni.origin !== null && !templateOriginIds.has(dni.origin.id),
    );

    orphanedDnItems.forEach((dnItem) => {
      const orphanFormItem = {
        ...dnItem.origin,
        id: isAddMode ? null : dnItem.id,
        origin_id: dnItem.origin.id,
        amount: formatAmount(dnItem.amount),
        attributes: dnItem.attributes.map((dnAttr) => ({
          ...dnAttr,
          id: isAddMode ? null : dnAttr.id,
          origin_id: dnAttr.origin.id,
          isNewChoice: false,
        })),
        highlightBg: true,
        isAdditionalItem: false,
        removedFromTemplate: true,
      };

      const dnGroupName = dnItem.groups?.[0]?.name;
      const targetGroup = dnGroupName ? item_groups.find((g) => g.name === dnGroupName) : null;
      if (targetGroup) {
        targetGroup.items.push(orphanFormItem);
      } else {
        const customGroup = item_groups.find((g) => g.id === null);
        const lastIdx = customGroup.items.length - 1;
        customGroup.items.splice(lastIdx, 0, orphanFormItem);
      }
    });
  }

  // Prepend auto-group with frequently used items and deduplicate
  if (autoItems.length > 0) {
    // In edit mode, map DN items with amount by origin ID so they stay in the auto group
    const dnItemsByOriginId = new Map();
    (deliveryNote?.items || [])
      .filter((dni) => dni.origin && dni.amount)
      .forEach((dni) => {
        dnItemsByOriginId.set(dni.origin.id, dni);
      });

    // In edit mode, exclude items already on the DN unless they have an amount
    const filteredAutoItems = deliveryNote
      ? autoItems.filter((ai) => {
          const dnItem = dnItemsByOriginId.get(ai.item.id);
          // Keep if not on DN at all, or if on DN with an amount
          return !deliveryNote.items.some((dni) => dni.origin?.id === ai.item.id) || !!dnItem;
        })
      : autoItems;

    if (filteredAutoItems.length > 0) {
      const autoOriginIds = new Set(filteredAutoItems.map((ai) => ai.item.id));

      // Build template position index: origin_id -> { groupOrder, itemOrder }
      const templatePositionMap = new Map();
      template.items.forEach((item, itemIdx) => {
        const groupId = item.groups?.[0]?.id ?? "custom";
        const groupOrder = groupIdsOrder.indexOf(groupId);
        templatePositionMap.set(item.id, { groupOrder, itemOrder: itemIdx });
      });

      const autoGroupItems = filteredAutoItems.map((ai) => {
        const dnItem = dnItemsByOriginId.get(ai.item.id);
        // Restore saved DN data for items that were already filled in
        if (dnItem) {
          return {
            ...ai.item,
            id: isAddMode ? null : dnItem.id,
            origin_id: dnItem.origin.id,
            amount: formatAmount(dnItem.amount),
            attributes: dnItem.attributes.map((dnAttr) => ({
              ...dnAttr,
              id: isAddMode ? null : dnAttr.id,
              origin_id: dnAttr.origin.id,
              isNewChoice: false,
            })),
            highlightBg: true,
            isAdditionalItem: false,
            removedFromTemplate: !templateOriginIds.has(dnItem.origin.id),
            originalGroupName: ai.item.groups?.[0]?.name || null,
          };
        }
        return {
          ...ai.item,
          id: null,
          origin_id: ai.item.id,
          amount: formatAmount(ai.item?.default_amount || ""),
          attributes: ai.item.attributes.map((attr) => ({
            ...attr,
            id: null,
            origin_id: attr.id,
            isNewChoice: false,
          })),
          highlightBg: false,
          isAdditionalItem: false,
          removedFromTemplate: !templateOriginIds.has(ai.item.id),
          originalGroupName: ai.item.groups?.[0]?.name || null,
        };
      });

      // Sort auto-items by original template position (group order, then item order)
      autoGroupItems.sort((a, b) => {
        const posA = templatePositionMap.get(a.origin_id) || {
          groupOrder: 999,
          itemOrder: 999,
        };
        const posB = templatePositionMap.get(b.origin_id) || {
          groupOrder: 999,
          itemOrder: 999,
        };
        return posA.groupOrder - posB.groupOrder || posA.itemOrder - posB.itemOrder;
      });

      // Remove auto-items from template groups to avoid duplicates
      item_groups = item_groups.map((group) => ({
        ...group,
        items: group.items.filter((item) => !autoOriginIds.has(item.origin_id)),
      }));

      item_groups.unshift({
        id: "auto",
        name: t("delivery_notes.auto_items_group", { notes: lastNotes }),
        items: autoGroupItems,
      });
    }
  }

  // Sort groups by order in template (custom at end if not present)
  item_groups.sort((a, b) => {
    if (a.id === "auto") return -1;
    if (b.id === "auto") return 1;
    const ia = groupIdsOrder.indexOf(a.id ?? "custom");
    const ib = groupIdsOrder.indexOf(b.id ?? "custom");
    return ia - ib;
  });

  return {
    version: version,
    state: DELIVERY_NOTE_STATE.PENDING,
    customer_signature: isAddMode ? "" : deliveryNote?.customer_signature || "",
    customer_signature_name: isAddMode ? "" : deliveryNote?.customer_signature_name || "",
    customer_signature_date: isAddMode ? "" : deliveryNote?.customer_signature_date || "",
    employee_signature: isAddMode ? me.signature || "" : deliveryNote?.employee_signature || "",
    employee_signature_name: isAddMode ? me.name || "" : deliveryNote?.employee_signature_name || "",
    customer: { ...customer },
    customer_property: { ...customerProperty },
    item_groups,
    remark: deliveryNote?.remark || "",
    uploads: [],
    comments: comments,
    documentation_schema: documentation_schema,
    documentation: documentation,
    coworker_ids: isAddMode
      ? (deliveryNote?.coworkers?.map((c) => c.id) || []).filter((id) => id !== me.id)
      : deliveryNote?.coworkers?.map((c) => c.id) || [],
    performed_by_id: isAddMode
      ? (me.roles?.map((r) => r.name) || []).includes(Role.TECHNICIAN)
        ? me.id
        : (deliveryNote?.performed_by?.id ?? null)
      : (deliveryNote?.performed_by?.id ?? null),
  };
};

const createValidationSchema = (t) =>
  Yup.object().shape({
    state: Yup.string().oneOf(Object.values(DELIVERY_NOTE_STATE)),
    customer: Yup.object().shape({
      id: Yup.number().nullable(),
      number: Yup.string(),
    }),
    customer_property: Yup.object().shape({
      id: Yup.number().nullable(),
      number: Yup.string(),
      //name: Yup.string().required(),
    }),
    item_groups: Yup.array().of(
      Yup.object().shape({
        id: Yup.mixed().nullable(),
        name: Yup.string(),
        description: Yup.string(),

        items: Yup.array().of(
          Yup.object().shape({
            id: Yup.number().nullable(),
            number: Yup.string().test(
              "no-existing-item-number",
              t("delivery_notes.errors.number_already_exists"),
              function (value) {
                if (!value) return true;
                const { from } = this;
                const group = from?.[1]?.value;
                if (group?.id !== null) return true;
                const allGroups = from?.[2]?.value?.item_groups ?? [];
                return !allGroups.some((g) => g.id !== null && g.items.some((i) => i.number === value));
              },
            ),
            name: Yup.string(),
            description: Yup.string(),
            amount: Yup.string()
              .transform((value) => (typeof value === "string" ? value.replace(",", ".") : value))
              .matches(/^\d+(\.\d+)?$/, t("errors.types.number.number"))
              .test("positive", t("errors.types.number.positive"), (value) => _.isEmpty(value) || Number(value) > 0),
            attributes: Yup.array().of(Yup.object().shape({})),
            //amount: Yup.number()
            //  .positive(t("errors.types.number.positive"))
            //  .nullable()
            //  .typeError(t("errors.types.number.number")),
          }),
        ),
      }),
    ),
    customer_signature_name: Yup.string(),
    customer_signature: Yup.string(),
    performed_by_id: Yup.number()
      .nullable()
      .when("state", {
        is: (s) => s && s !== DELIVERY_NOTE_STATE.DRAFTED,
        then: (schema) =>
          schema.typeError(t("delivery_notes.errors.performed_by_required")).required(
            t("delivery_notes.errors.performed_by_required"),
          ),
      }),
    uploads: Yup.array().of(
      Yup.object().shape({
        url: Yup.string().required(),
        hash: Yup.string().required(),
        name: Yup.string().required(),
        size: Yup.number().required(),
        type: Yup.string().required(),
      }),
    ),
  });

function UnsavedChangesGuard({ when }) {
  useEffect(() => {
    if (!when) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = ""; // legacy Chrome requires returnValue to be set
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [when]);
  return null;
}

UnsavedChangesGuard.propTypes = {
  when: PropTypes.bool.isRequired,
};

function SaveErrorAlert({ variant, isOnline }) {
  const { t } = useTranslation();

  if (variant === SAVE_ERROR.CONFLICT) {
    return (
      <div className="alert alert-danger mb-2" role="alert">
        {t("delivery_notes.alerts.save_conflict")}
      </div>
    );
  }

  const alertClassName = isOnline ? "alert-success" : "alert-warning";
  const message = isOnline
    ? t("delivery_notes.alerts.save_offline_restored")
    : t("delivery_notes.alerts.save_offline");

  return (
    <div className={`alert ${alertClassName} mb-2`} role="alert">
      {message}
    </div>
  );
}

SaveErrorAlert.propTypes = {
  variant: PropTypes.oneOf(Object.values(SAVE_ERROR)).isRequired,
  isOnline: PropTypes.bool.isRequired,
};

function DeliveryNoteAddEdit() {
  const { t } = useTranslation();
  const params = useParams();
  const noteId = Number.parseInt(params.noteId);
  const isAddMode = !noteId;
  const navigate = useNavigate();
  const addToast = useToastStore((state) => state.addToast);
  const customerSignatureRef = useRef();
  const employeeSignatureRef = useRef();
  const remarkRef = useRef("");
  const remarkInitializedRef = useRef(false);

  const [lockCustomerSignatureField, setLockCustomerSignatureField] = useState(false);
  const [lockEmployeeSignatureField, setLockEmployeeSignatureField] = useState(false);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [isSearchingCustomer, setIsSearchingCustomer] = useState(false);
  const [customerSearchError, setCustomerSearchError] = useState(null);
  const [isLoadingProperty, setIsLoadingProperty] = useState(false);
  const [articleSearch, setArticleSearch] = useState("");
  const [propertyContacts, setPropertyContacts] = useState([]);
  const [propertyCommunications, setPropertyCommunications] = useState([]);
  const [remarkCollapsed, setRemarkCollapsed] = useState(true);
  const [showConfirmFinalize, setShowConfirmFinalize] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [autoItems, setAutoItems] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(INITIAL_UPLOAD_STATUS);
  const [attachmentUploaderKey, setAttachmentUploaderKey] = useState(0);

  const isOnline = useOnlineStatus();

  const [queryParams, setQueryParams] = useSearchParams();

  const { me, hasRole, updateSignature } = useAuthStore(
    useShallow((state) => ({
      me: state.me,
      hasRole: state.hasRole,
      updateSignature: state.updateSignature,
    })),
  );
  const features = useSettingsStore((state) => state.features);
  const { tunnelAvailable, fetchTunnelStatus } = useNatravStore(
    useShallow((state) => ({
      tunnelAvailable: state.available,
      fetchTunnelStatus: state.fetchStatus,
    })),
  );

  const deliveryNoteId = isAddMode ? parseInt(queryParams.get("copyFromId")) || null : noteId;
  const workOrderId = isAddMode ? parseInt(queryParams.get("workOrderId")) || null : null;

  const {
    deliveryNotesByIds,
    createOneDeliveryNote,
    updateOneDeliveryNote,
    getDeliveryNoteById,
    deliveryNoteIsInState,
    deliveryNoteIsOwner,
    deleteDeliveryNoteFile,
  } = useDeliveryNotesStore(
    useShallow((state) => ({
      deliveryNotesByIds: state.byIds,
      createOneDeliveryNote: state.createOne,
      updateOneDeliveryNote: state.updateOne,
      getDeliveryNoteById: state.getById,
      deliveryNoteIsInState: state.isInState,
      deliveryNoteIsOwner: state.isOwner,
      deleteDeliveryNoteFile: state.deleteFile,
    })),
  );

  const { workOrdersByIds, getWorkOrderById } = useWorkOrdersStore(
    useShallow((state) => ({
      workOrdersByIds: state.byIds,
      getWorkOrderById: state.getById,
    })),
  );
  const workOrder = Number.isInteger(workOrderId) ? workOrdersByIds[workOrderId] : null;

  const deliveryNote = Number.isInteger(deliveryNoteId) ? deliveryNotesByIds[deliveryNoteId] : null;

  if (deliveryNote?.remark && !remarkInitializedRef.current) {
    remarkRef.current = deliveryNote.remark;
    remarkInitializedRef.current = true;
  }

  const isCreator = deliveryNoteIsOwner(deliveryNote, me);
  const isPerformer = !!deliveryNote && deliveryNote.performed_by?.id === me.id;
  const isAssignee = !!deliveryNote && deliveryNote.assigned_to?.id === me.id;

  const isInDraftedState = deliveryNoteIsInState(deliveryNote, DELIVERY_NOTE_STATE.DRAFTED);

  const isInDeliveredState = deliveryNoteIsInState(deliveryNote, DELIVERY_NOTE_STATE.DELIVERED);

  const isManager = hasRole(Role.ADMINISTRATOR) || hasRole(Role.MANAGER) || hasRole(Role.OFFICE);
  const canEdit =
    hasRole(Role.ADMINISTRATOR) ||
    ((hasRole(Role.MANAGER) || hasRole(Role.OFFICE)) && !isInDraftedState) ||
    ((isCreator || isPerformer || isAssignee) && isInDraftedState);

  const shouldShowAutoItems = isAddMode || isInDraftedState;

  const templateId = isAddMode ? parseInt(queryParams.get("templateId")) || null : deliveryNote?.template.id || null;

  const { templateByIds, getTemplateById } = useDeliveryNoteTemplatesStore(
    useShallow((state) => ({
      templateByIds: state.byIds,
      getTemplateById: state.getById,
    })),
  );

  const template = Number.isInteger(templateId) ? templateByIds[templateId] : null;

  useEffect(() => {
    if (templateId) {
      getTemplateById({ id: templateId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  useEffect(() => {
    if (deliveryNoteId) {
      getDeliveryNoteById({ id: deliveryNoteId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryNoteId]);

  useEffect(() => {
    if (workOrderId) {
      getWorkOrderById({ id: workOrderId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderId]);

  useEffect(() => {
    getTechnicians().then((users) => {
      setTechnicians(users);
    });
    if (features.handel) {
      fetchTunnelStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!shouldShowAutoItems || !templateId) {
      setAutoItems([]);
      return;
    }
    getDeliveryNoteItems({
      templateId,
      createdBy: me.id,
      sortBy: "frequency",
      lastNotes: 20,
      limit: 15,
    }).then(({ data }) => {
      setAutoItems(data || []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowAutoItems, templateId]);

  const applyEntityData = (prefix, values, setFieldValue, data = { name: "", address: { street: "" } }) => {
    if (data.number !== undefined) {
      setFieldValue(`${prefix}.number`, data.number || values.number || "");
    }
    setFieldValue(`${prefix}.name`, data.name);
    setFieldValue(`${prefix}.is_fetched`, data?.is_fetched || false);
    setFieldValue(`${prefix}.fetched_fields`, {
      name: !!data.name,
      street: !!data.address?.street,
      zip: !!data.address?.zip,
      city: !!data.address?.city,
    });
    setFieldValue(`${prefix}.addresses[0]`, {
      ...values.addresses[0],
      street: data.address?.street || "",
      city: data.address?.city || "",
      zip: data.address?.zip || "",
      district: data.address?.district || "",
      country: data.address?.country?.name || "",
    });
  };

  const selectCustomerProperty = async (values, setFieldValue, options, number) => {
    const selected = options.find((o) => String(o.value.number) === String(number));
    const pick = selected || (options.length > 0 ? options[0] : null);
    if (pick) {
      setIsLoadingProperty(true);
      try {
        const [address, contacts, comms] = await Promise.all([
          getPropertyAddress(pick.value.slug),
          getPropertyContacts(pick.value.slug),
          getPropertyCommunications(pick.value.slug),
        ]);
        applyEntityData("customer_property", values, setFieldValue, {
          is_fetched: true,
          number: pick.value.number,
          name: address?.name,
          address,
        });
        setPropertyContacts(contacts || []);
        setPropertyCommunications(comms || []);
      } catch {
        setPropertyContacts([]);
        setPropertyCommunications([]);
      } finally {
        setIsLoadingProperty(false);
      }
    }
  };

  const matchesArticleSearch = (item) => {
    if (!articleSearch) return true;
    const term = articleSearch.toLowerCase();
    return item.number?.toLowerCase().includes(term) || item.name?.toLowerCase().includes(term);
  };

  const setSignaturePadLockState = (signaturePadRef, shouldLock) => {
    const signaturePad = signaturePadRef.current;
    if (!signaturePad) {
      return;
    }

    const canvas = signaturePad.getCanvas?.();

    if (shouldLock) {
      signaturePad.off();
      if (canvas) {
        canvas.style.touchAction = "auto";
      }
      return;
    }

    if (canvas) {
      canvas.style.touchAction = "none";
    }
    signaturePad.on();
  };

  const handleToggleEmployeeSignatureLock = () => {
    const nextLocked = !lockEmployeeSignatureField;
    setSignaturePadLockState(employeeSignatureRef, nextLocked);
    setLockEmployeeSignatureField(nextLocked);
  };

  const handleToggleCustomerSignatureLock = () => {
    const nextLocked = !lockCustomerSignatureField;
    setSignaturePadLockState(customerSignatureRef, nextLocked);
    setLockCustomerSignatureField(nextLocked);
  };

  const handleSubmit = async (
    values,
    { setErrors, setFieldValue, setSubmitting },
  ) => {
    // only items which have some amount are submitted to backend
    const items = values.item_groups
      .flatMap((g) => g.items)
      .map((i) => ({ ...i, amount: (i.amount ?? "").replace(",", ".") }))
      .filter((i) => !_.isEmpty(i.amount));

    // only submit comments which have some text
    const comments = values.comments.filter((c) => !_.isEmpty(c.text));

    // only submit documentation rows where at least one cell differs from the template default
    const documentation = buildDocumentationPayload(values, template);

    const func = isAddMode ? createOneDeliveryNote : updateOneDeliveryNote;

    const canEditSignatures = isAddMode || isInDraftedState;

    const employeeSignature =
      canEditSignatures && !!employeeSignatureRef.current && !employeeSignatureRef.current.isEmpty()
        ? employeeSignatureRef.current.getTrimmedCanvas().toDataURL("image/png")
        : values.employee_signature;

    setSaveError(null);

    try {
      const { data, error } = await func({
        id: noteId,
        version: values.version,
        state: values.state,
        template: template,
        customer: values.customer,
        customer_property: values.customer_property,
        items: items.map((item) => ({
          // items_custom has no id
          id: _.defaultTo(item.id, null),
          origin_id: _.defaultTo(item.origin_id, null),
          amount: item.amount,
          number: item.number,
          name: item.name,

          // items_custom has no attributes
          attributes: _.defaultTo(item.attributes, []),
        })),
        uploads: values.uploads,
        comments: comments,
        documentation_schema: values.documentation_schema,
        documentation: documentation,
        remark: values.remark,
        customer_signature:
          canEditSignatures && !!customerSignatureRef.current && !customerSignatureRef.current.isEmpty()
            ? customerSignatureRef.current.getTrimmedCanvas().toDataURL("image/png")
            : null,
        customer_signature_name:
          canEditSignatures && values.customer_signature_name ? values.customer_signature_name : null,
        customer_signature_date:
          canEditSignatures && !!customerSignatureRef.current && !customerSignatureRef.current.isEmpty()
            ? values.customer_signature_date || null
            : null,
        employee_signature: employeeSignature,
        employee_signature_name: canEditSignatures && !!employeeSignature ? values.employee_signature_name : null,
        coworker_ids: values.coworker_ids,
        performed_by_id: values.performed_by_id,
        work_order_id: isAddMode ? workOrderId : undefined,
      });

      if (error) {
        //error.detail.forEach(d => setErrors)
        if (isNetworkError(error)) {
          setSaveError(SAVE_ERROR.OFFLINE);
        } else if (error.status === HTTP_CONFLICT) {
          setSaveError(SAVE_ERROR.CONFLICT);
        } else {
          showApiError(error, t);
        }
      } else {
        if (canEditSignatures && !!employeeSignature) {
          updateSignature(employeeSignature);
        }
        setFieldValue("uploads", [], false);
        setUploadStatus(INITIAL_UPLOAD_STATUS);
        setAttachmentUploaderKey((currentKey) => currentKey + 1);
        addToast("success", t("common.saved"));
        if (values.state === DELIVERY_NOTE_STATE.DELIVERED) {
          navigate(workOrderId ? `/work_orders/${workOrderId}/details` : "/delivery_notes");
        } else if (isAddMode && data?.id) {
          const currentTab = queryParams.get("tab");
          const target = currentTab
            ? `/delivery_notes/${data.id}/edit?tab=${currentTab}`
            : `/delivery_notes/${data.id}/edit`;
          navigate(target, { replace: true });
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Memoize so per-row _ids generated inside createInitialValues stay stable
  // across renders; otherwise enableReinitialize would reset user input.
  const initialValues = useMemo(() => {
    const noteNotReady = Number.isInteger(deliveryNoteId) && !deliveryNote;
    const workOrderNotReady = Number.isInteger(workOrderId) && !workOrder;
    if (!template || noteNotReady || workOrderNotReady || autoItems === null) return null;
    return createInitialValues(me, template, deliveryNote, isAddMode, autoItems, t, 20, workOrder);
  }, [me, template, deliveryNote, deliveryNoteId, isAddMode, autoItems, t, workOrderId, workOrder]);

  if (!template) {
    return <Spinner />;
  }

  if (Number.isInteger(deliveryNoteId) && !deliveryNote) {
    return <Spinner />;
  }

  if (Number.isInteger(workOrderId) && !workOrder) {
    return <Spinner />;
  }

  if (autoItems === null) {
    return <Spinner />;
  }

  // Initialize collapsed state once when all data is ready
  if (shouldShowAutoItems && autoItems.length > 0 && Object.keys(collapsedGroups).length === 0) {
    const initialCollapsed = {};
    const groupIds = [...new Set(template.items.map((i) => i.groups?.[0]?.id ?? "custom"))];
    if (!groupIds.includes("custom")) {
      groupIds.push("custom");
    }
    groupIds.forEach((gid) => {
      initialCollapsed[gid] = true;
    });
    initialCollapsed["auto"] = false;
    setCollapsedGroups(initialCollapsed);
  }

  const isReviewed = !isAddMode && deliveryNote.is_reviewed;
  const canSubmit = isAddMode || (canEdit && !isReviewed);

  return (
    <div>
      <Breadcrumb
        items={[
          { label: t("delivery_notes.delivery_notes"), to: "/delivery_notes" },
          { label: template.name, to: `/delivery_notes/add?templateId=${template.id}` },
          {
            label: isAddMode ? t("menu.add") : `${t("common.number_short")} ${deliveryNote.number}`,
          },
        ]}
      />

      {isReviewed && <div className="alert alert-danger">{t("delivery_notes.alerts.reviewed")}</div>}
      {!canSubmit && <div className="alert alert-danger">{t("permissions.can_not_edit")}</div>}
      {!isOnline && <div className="alert alert-warning">{t("common.offline")}</div>}

      <Formik
        initialValues={initialValues}
        validationSchema={createValidationSchema(t)}
        validateOnChange={true}
        validateOnBlur={false}
        enableReinitialize={true}
        validateOnMount={false}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          dirty,
          handleChange,
          handleBlur,
          submitForm,
          isSubmitting,
          setValues,
          setFieldValue,
          /* and other goodies */
        }) => {
          const hasDocumentationTab = true;
          const hasCoworkersTab = technicians.length > 0;
          const saveDisabled =
            isSubmitting ||
            !canSubmit ||
            uploadStatus.isBusy ||
            uploadStatus.hasErrors;
          const rawTab = queryParams.get("tab");
          const tab =
            VALID_TABS.includes(rawTab) &&
            (rawTab !== TABS.COWORKERS || hasCoworkersTab)
              ? rawTab
              : TABS.NOTE;
          const handleTabChange = (nextTab) => (e) => {
            e.preventDefault();
            if (nextTab === tab) return;
            const params = new URLSearchParams(queryParams);
            if (nextTab === TABS.NOTE) params.delete("tab");
            else params.set("tab", nextTab);
            setQueryParams(params);
          };
          const offlineNotice = !isOnline && !saveError && (
            <div className="alert alert-warning mb-2">{t("common.offline")}</div>
          );
          const saveErrorAlert = saveError && (
            <SaveErrorAlert variant={saveError} isOnline={isOnline} />
          );
          return (
          <Form>
            <UnsavedChangesGuard when={dirty || !!saveError} />
            <ul className="nav nav-pills mb-2 mx-2 mx-md-0">
              <li className="nav-item">
                <a
                  href="#tab-note"
                  className={`nav-link${tab === TABS.NOTE ? " active" : ""}`}
                  onClick={handleTabChange(TABS.NOTE)}
                >
                  <i className="fa fa-file-text fa-fw" />
                  <span className="ms-1">Bericht</span>
                </a>
              </li>
              {hasDocumentationTab && (
                <li className="nav-item">
                  <a
                    href="#tab-doc"
                    className={`nav-link${tab === TABS.DOCUMENTATION ? " active" : ""}`}
                    onClick={handleTabChange(TABS.DOCUMENTATION)}
                  >
                    <i className="fa fa-book fa-fw" />
                    <span className="d-inline d-md-none ms-1">{t("menu.documentation_short")}</span>
                    <span className="d-none d-md-inline ms-1">{t("menu.documentation")}</span>
                  </a>
                </li>
              )}
              <li className="nav-item">
                <a
                  href="#tab-com"
                  className={`nav-link${tab === TABS.COMMENTS ? " active" : ""}`}
                  onClick={handleTabChange(TABS.COMMENTS)}
                >
                  <i className="fa fa-comments fa-fw" />
                  <span className="d-none d-md-inline ms-1">{t("menu.comments")}</span>
                </a>
              </li>
              {hasCoworkersTab && (
                <li className="nav-item">
                  <a
                    href="#tab-coworkers"
                    className={`nav-link${tab === TABS.COWORKERS ? " active" : ""}`}
                    onClick={handleTabChange(TABS.COWORKERS)}
                  >
                    <i className="fa fa-users fa-fw" />
                    <span className="d-none d-md-inline ms-1">{t("delivery_notes.coworker_badge")}</span>
                  </a>
                </li>
              )}
            </ul>

            <div className="tab-content">
              <div className={`tab-pane fade${tab === TABS.NOTE ? " active show" : ""}`} id="tab-note">
                {!features.handel && (
                  <div className="alert alert-warning mb-3">
                    <i className="fa fa-exclamation-triangle me-2" />
                    {t("customers.search_unavailable")}
                  </div>
                )}
                {features.handel && tunnelAvailable === false && (
                  <div className="alert alert-warning mb-3">
                    <i className="fa fa-exclamation-triangle me-2" />
                    {t("customers.service_unavailable")}
                  </div>
                )}
                <div className="row gx-0 gx-lg-4">
                  <div className="col-12 col-lg">
                    <CustomerLookupCard
                      icon="fa-users"
                      title={t("customers.customer")}
                      fieldPrefix="customer"
                      values={values.customer}
                      setFieldValue={setFieldValue}
                      isSearching={isSearchingCustomer}
                      isLoading={isSearchingCustomer}
                      searchError={customerSearchError}
                      fetchedFields={values.customer?.fetched_fields}
                      numberLabel={t("customers.number")}
                      nameLabel={t("customers.name")}
                      nameDisabled={!!values.customer?.fetched_fields?.name}
                      onNumberChange={async (e) => {
                        setFieldValue("customer.number", e.target.value);
                        if (features.handel) {
                          applyEntityData("customer", values.customer, setFieldValue);
                          applyEntityData("customer_property", values.customer_property, setFieldValue);
                          setCustomerSearchError(null);
                          setPropertyOptions([]);
                          setPropertyContacts([]);
                          setPropertyCommunications([]);
                        }
                      }}
                      searchDisabled={!features.handel}
                      searchDisabledTooltip={t("customers.search_unavailable")}
                      onSearch={async () => {
                        if (_.isEmpty(values.customer.number)) return;
                        setIsSearchingCustomer(true);
                        try {
                          const { data } = await searchCustomerByNumber({
                            number: values.customer.number,
                          });
                          if (_.isEmpty(data)) {
                            applyEntityData("customer", values.customer, setFieldValue);
                            setCustomerSearchError("customers.not_found");
                            setPropertyOptions([]);
                            setPropertyContacts([]);
                            setPropertyCommunications([]);
                          } else {
                            const customerId = data[0].slug;
                            const [address, properties] = await Promise.all([
                              getCustomerAddress(customerId),
                              getCustomerProperties(customerId),
                            ]);
                            setCustomerSearchError(null);
                            applyEntityData("customer", values.customer, setFieldValue, {
                              is_fetched: true,
                              name: address.name,
                              address,
                            });
                            const options = (properties || []).map((p) => ({
                              value: p,
                              label: p.number,
                            }));
                            setPropertyOptions(options);
                            selectCustomerProperty(
                              values.customer_property,
                              setFieldValue,
                              options,
                              values.customer_property.number,
                            );
                          }
                        } catch {
                          setCustomerSearchError("customers.service_unavailable");
                        } finally {
                          setIsSearchingCustomer(false);
                        }
                      }}
                    />
                  </div>
                  <div className="col-12 col-lg">
                    <CustomerLookupCard
                      icon="fa-sitemap"
                      title={t("customers.properties.property")}
                      fieldPrefix="customer_property"
                      values={values.customer_property}
                      setFieldValue={setFieldValue}
                      isLoading={isLoadingProperty}
                      fetchedFields={values.customer_property?.fetched_fields}
                      propertyOptions={propertyOptions}
                      numberLabel={t("customers.properties.number")}
                      nameLabel={t("customers.properties.name")}
                      nameDisabled={propertyOptions.length > 0}
                      contacts={propertyContacts}
                      communications={propertyCommunications}
                      onSelectOption={(selectedNumber) =>
                        selectCustomerProperty(values.customer_property, setFieldValue, propertyOptions, selectedNumber)
                      }
                    />
                  </div>
                </div>

                <Search search={articleSearch} handleSearch={(e) => setArticleSearch(e.target.value)} />

                <div className="row gx-0">
                  <FieldArray
                    name="item_groups"
                    render={(arrayHelpers) => (
                      <Fragment>
                        {values.item_groups.map((group, group_index) => {
                          const hasMatchingItems = group.items.some(matchesArticleSearch);
                          if (group.items.length === 0) return null;
                          if (articleSearch && !hasMatchingItems) return null;
                          const isAutoGroup = group.id === "auto";
                          const isCollapsed = !!collapsedGroups[group.id ?? "custom"];
                          const filledCount = group.items.filter((i) => !!i.amount).length;
                          const groupIcon = isAutoGroup ? "fa-star" : "fa-cog";
                          const accentClass = isAutoGroup ? "border-start border-primary border-3" : "";
                          return (
                            <div key={`group-${group.id}`} className="col-12">
                              <div className={`card border-0 mb-4 ${accentClass}`}>
                                <div
                                  className="card-header h6 mb-0 bg-none p-3 d-flex align-items-center"
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    setCollapsedGroups((prev) => ({
                                      ...prev,
                                      [group.id ?? "custom"]: !prev[group.id ?? "custom"],
                                    }))
                                  }
                                >
                                  <i
                                    className={`fa ${
                                      isCollapsed ? "fa-chevron-right" : "fa-chevron-down"
                                    } fa-fw text-dark text-opacity-50 me-1 d-md-none`}
                                  />
                                  <i
                                    className={`fa ${groupIcon} fa-lg fa-fw me-1 ${isAutoGroup ? "text-warning" : "text-dark text-opacity-50"}`}
                                  />{" "}
                                  {group.name}
                                  {isCollapsed && filledCount > 0 && (
                                    <span className="badge bg-danger ms-2 d-md-none">{filledCount}</span>
                                  )}
                                </div>
                                <div className={isCollapsed ? "d-none d-md-block" : ""}>
                                  <FieldArray
                                    name={`item_groups.${group_index}.items`}
                                    render={(arrayHelpersItems) => (
                                      <div className="card-body">
                                        {group.items.map((item, item_index) => {
                                          if (articleSearch && !matchesArticleSearch(item)) return null;
                                          return (
                                            <Fragment key={`item-${item.id}-${item_index}`}>
                                              <div className="row align-items-center">
                                                {(!_.isNull(group.id) || isManager) && (
                                                  <div className="col-12 col-md-3 mb-1 mb-md-0">
                                                    {!_.isNull(group.id) ? (
                                                      <strong>{item.number}</strong>
                                                    ) : (
                                                      <>
                                                        <Field
                                                          name={`item_groups.${group_index}.items.${item_index}.number`}
                                                          className={`form-control ${
                                                            item?.highlightBg && "bg-yellow-100 bg-opacity-25"
                                                          } ${
                                                            getIn(
                                                              errors,
                                                              `item_groups.${group_index}.items.${item_index}.number`,
                                                            )
                                                              ? "is-invalid"
                                                              : ""
                                                          }`}
                                                          placeholder={t("items.number")}
                                                        />
                                                        <ErrorMessage
                                                          name={`item_groups.${group_index}.items.${item_index}.number`}
                                                          render={(error) => (
                                                            <div className="invalid-feedback">{t(error)}</div>
                                                          )}
                                                        />
                                                      </>
                                                    )}
                                                  </div>
                                                )}
                                                <div className="col-12 col-md-6 mb-1 ps-md-0">
                                                  {!_.isNull(group.id) || item.removedFromTemplate ? (
                                                    <>
                                                      {item.name}
                                                      {item.removedFromTemplate && (
                                                        <span
                                                          className="badge bg-warning bg-opacity-15 text-warning ms-2"
                                                          title={t("delivery_notes.removed_from_template", { name: template?.name })}
                                                        >
                                                          <i className="fa fa-exclamation-triangle me-1" />
                                                          {t("delivery_notes.removed_from_template", { name: template?.name })}
                                                        </span>
                                                      )}
                                                    </>
                                                  ) : (
                                                    <Field
                                                      name={`item_groups.${group_index}.items.${item_index}.name`}
                                                      className={`form-control ${
                                                        item?.highlightBg && "bg-yellow-100 bg-opacity-25"
                                                      } ${
                                                        getIn(
                                                          errors,
                                                          `item_groups.${group_index}.items.${item_index}.name`,
                                                        )
                                                          ? "is-invalid"
                                                          : ""
                                                      }`}
                                                      placeholder={t("items.name")}
                                                    />
                                                  )}
                                                  {_.isNull(group.id) && (
                                                    <ErrorMessage
                                                      name={`item_groups.${group_index}.items.${item_index}.name`}
                                                      render={(error) => (
                                                        <div className="invalid-feedback">{t(error)}</div>
                                                      )}
                                                    />
                                                  )}
                                                  {!!item.inspect_every_x_months && (
                                                    <span className="mx-1 badge bg-yellow-100 fs-10px rounded-pill text-dark">
                                                      {item.inspect_every_x_months} {t("common.months")}
                                                    </span>
                                                  )}
                                                  {!!item.originalGroupName && (
                                                    <span className="mx-1 badge bg-gray-200 fs-10px rounded-pill text-dark">
                                                      {item.originalGroupName}
                                                    </span>
                                                  )}
                                                </div>
                                                <div className="col-12 col-md">
                                                  <div className="input-group">
                                                    <Field
                                                      name={`item_groups.${group_index}.items.${item_index}.amount`}
                                                      //pattern="[0-9]*"
                                                      inputMode="decimal"
                                                      disabled={item.characteristic === ITEM_CHARACTERISTIC.UNIQUE}
                                                      className={`form-control ${
                                                        item?.highlightBg && "bg-yellow-100 bg-opacity-25"
                                                      } ${
                                                        getIn(
                                                          errors,
                                                          `item_groups.${group_index}.items.${item_index}.amount`,
                                                        )
                                                          ? "is-invalid"
                                                          : ""
                                                      }`}
                                                      placeholder={t("items.amount")}
                                                    />

                                                    {/* hanlde non custom items */}
                                                    {group.id !== null &&
                                                      item.characteristic !== ITEM_CHARACTERISTIC.DIFFERENT && (
                                                        <button
                                                          type="button"
                                                          onClick={() => {
                                                            if (!item.amount) {
                                                              setFieldValue(
                                                                `item_groups.${group_index}.items.${item_index}.amount`,
                                                                "1",
                                                              );
                                                            } else {
                                                              arrayHelpersItems.insert(item_index + 1, {
                                                                ...item,
                                                                id: null,
                                                                attributes: item.attributes.map((attr) => ({
                                                                  ...attr,
                                                                  id: null,
                                                                })),
                                                                isAdditionalItem: true,
                                                              });
                                                            }
                                                          }}
                                                          className="btn btn-success"
                                                        >
                                                          <i className="fa fa-plus" />{" "}
                                                        </button>
                                                      )}
                                                    {/* hanlde non custom items */}
                                                    {group.id !== null &&
                                                      item.characteristic !== ITEM_CHARACTERISTIC.DIFFERENT && (
                                                        <button
                                                          type="button"
                                                          disabled={!item.amount}
                                                          onClick={() => {
                                                            if (item.isAdditionalItem) {
                                                              arrayHelpersItems.remove(item_index);
                                                            } else {
                                                              setFieldValue(
                                                                `item_groups.${group_index}.items.${item_index}.amount`,
                                                                "",
                                                              );
                                                            }
                                                          }}
                                                          className={`btn btn-danger`}
                                                        >
                                                          <i className="fa fa-minus" />{" "}
                                                        </button>
                                                      )}
                                                    {group.id === null && (
                                                      <button
                                                        type="button"
                                                        className="btn btn-success"
                                                        onClick={() =>
                                                          arrayHelpersItems.insert(item_index + 1, {
                                                            number: "",
                                                            name: "",
                                                            amount: "",
                                                            origin_id: null,
                                                            attributes: [],
                                                            highlightBg: false,
                                                            isAdditionalItem: false,
                                                          })
                                                        }
                                                      >
                                                        <i className="fa fa-plus" />
                                                      </button>
                                                    )}
                                                    {group.id === null && (
                                                      <button
                                                        type="button"
                                                        className="btn btn-danger"
                                                        //disabled={item_index === 0}
                                                        onClick={() => {
                                                          if (_.eq(item_index, 0)) {
                                                            setFieldValue(
                                                              `item_groups.${group_index}.items.${item_index}`,
                                                              {
                                                                number: "",
                                                                name: "",
                                                                amount: "",
                                                                origin_id: null,
                                                                attributes: [],
                                                                highlightBg: false,
                                                                isAdditionalItem: false,
                                                              },
                                                            );
                                                          } else {
                                                            arrayHelpersItems.remove(item_index);
                                                          }
                                                        }}
                                                      >
                                                        <i className="fa fa-minus" />
                                                      </button>
                                                    )}
                                                    <ErrorMessage
                                                      name={`item_groups.${group_index}.items.${item_index}.amount`}
                                                      render={(error) => (
                                                        <div className="invalid-feedback">{t(error)}</div>
                                                      )}
                                                    />
                                                  </div>
                                                </div>
                                              </div>
                                              {!!item.amount &&
                                                item.attributes.map((attr, attr_index) => (
                                                  <div
                                                    key={`${attr.id}-${attr_index}`}
                                                    className="row align-items-center mt-1"
                                                  >
                                                    <div className="col-4 col-md-3 d-none d-md-flex"></div>
                                                    <div className="col col-md-6 p-md-0">{attr.name}</div>
                                                    <div className="col-12 col-md">
                                                      {attr.type !== "select" || attr.isNewChoice ? (
                                                        <div className="input-group">
                                                          <Field
                                                            name={`item_groups.${group_index}.items.${item_index}.attributes.${attr_index}.data.values[0]`}
                                                            placeholder={
                                                              attr.isNewChoice
                                                                ? t("items.attributes.menu.add_choice")
                                                                : ""
                                                            }
                                                            className={`form-control ${
                                                              item?.highlightBg && "bg-yellow-100 bg-opacity-25"
                                                            } ${
                                                              getIn(
                                                                errors,
                                                                `item_groups.${group_index}.items.${item_index}.attributes.${attr_index}.data.values[0]`,
                                                              )
                                                                ? "is-invalid"
                                                                : ""
                                                            }`}
                                                          />
                                                          {attr.type === "select" && (
                                                            <button
                                                              type="button"
                                                              className="btn btn-warning"
                                                              placeholder={t("items.attributes.menu.select_choice")}
                                                              onClick={() => {
                                                                setFieldValue(
                                                                  `item_groups.${group_index}.items.${item_index}.attributes.${attr_index}.isNewChoice`,
                                                                  false,
                                                                );
                                                                setFieldValue(
                                                                  `item_groups.${group_index}.items.${item_index}.attributes.${attr_index}.data.values`,
                                                                  values.item_groups[group_index].items[item_index]
                                                                    .attributes[attr_index]._tmp.original.values,
                                                                );
                                                              }}
                                                            >
                                                              <span className="fa fa-pencil"></span>
                                                            </button>
                                                          )}
                                                        </div>
                                                      ) : (
                                                        <div className="input-group">
                                                          <Field
                                                            as={"select"}
                                                            name={`item_groups.${group_index}.items.${item_index}.attributes.${attr_index}.data.values[0]`}
                                                            className={`form-select ${
                                                              item?.highlightBg && "bg-yellow-100 bg-opacity-25"
                                                            } ${
                                                              getIn(
                                                                errors,
                                                                `item_groups.${group_index}.attributes.${attr_index}.data.values[0]`,
                                                              )
                                                                ? "is-invalid"
                                                                : ""
                                                            }`}
                                                            placeholder={t("items.amount")}
                                                          >
                                                            {attr.data?.choices.map((choice, i) => (
                                                              <option key={choice} value={choice}>
                                                                {choice}
                                                              </option>
                                                            ))}
                                                          </Field>
                                                          <button
                                                            type="button"
                                                            className="btn btn-success"
                                                            onClick={() => {
                                                              //setSelectedAttributeId(attr.id)
                                                              setFieldValue(
                                                                `item_groups.${group_index}.items.${item_index}.attributes.${attr_index}.isNewChoice`,
                                                                true,
                                                              );
                                                              setFieldValue(
                                                                `item_groups.${group_index}.items.${item_index}.attributes.${attr_index}._tmp.original.values`,
                                                                values.item_groups[group_index].items[item_index]
                                                                  .attributes[attr_index].data.values,
                                                              );
                                                              setFieldValue(
                                                                `item_groups.${group_index}.items.${item_index}.attributes.${attr_index}.data.values[0]`,
                                                                "",
                                                              );
                                                            }}
                                                          >
                                                            <span className="fa fa-plus"></span>
                                                          </button>
                                                        </div>
                                                      )}

                                                      <ErrorMessage
                                                        name={`item_groups.${group_index}.items.${item_index}.attributes.${attr_index}.data.values[0]`}
                                                        render={(error) => (
                                                          <div className="invalid-feedback">{t(error)}</div>
                                                        )}
                                                      />
                                                    </div>
                                                  </div>
                                                ))}
                                              <hr className="mt-2 mb-2" />
                                            </Fragment>
                                          );
                                        })}
                                      </div>
                                    )}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </Fragment>
                    )}
                  />
                </div>

                <div className="row gx-0">
                  <div className="col">
                    <div className="card border-0 mb-4">
                      <div className="card-header h6 mb-0 bg-none p-3">
                        <i className="fa fa-paperclip fa-lg fa-fw text-dark text-opacity-50 me-1"></i> {t("menu.files")}
                      </div>
                      <div className="card-body">
                        <div className="row align-items-center">
                          <div className="col">
                            <AttachmentUploader
                              key={attachmentUploaderKey}
                              metadata={{ userId: me.id }}
                              onUploadsChange={(uploads) =>
                                setFieldValue("uploads", uploads, false)
                              }
                              onStatusChange={setUploadStatus}
                            />
                          </div>
                        </div>
                        {!isAddMode && (
                          <div className="row mt-3">
                            <div className="col">
                              <div className="flot-start">
                                <ImageThumbnailGallery
                                  files={deliveryNote.files}
                                  onDelete={
                                    isInDraftedState || isInDeliveredState
                                      ? (fileId) => deleteDeliveryNoteFile({ id: deliveryNoteId, fileId })
                                      : undefined
                                  }
                                  canDelete={(file) => file.created_by?.id === me.id}
                                  showFileDetails={false}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row gx-0">
                  <div className="col">
                    <div className="card border-0 mb-4">
                      <div
                        className="card-header h6 mb-0 bg-none p-3 d-flex align-items-center"
                        style={{ cursor: "pointer" }}
                        onClick={() => setRemarkCollapsed((prev) => !prev)}
                      >
                        <i className={`fa fa-chevron-${remarkCollapsed ? "right" : "down"} fa-fw me-1 d-md-none`}></i>
                        <i className="fa fa-paperclip fa-lg fa-fw text-dark text-opacity-50 me-1"></i>{" "}
                        {t("menu.remark")}
                        <span className="ms-auto" onClick={(e) => e.stopPropagation()}>
                          <AudioRecordButton
                            onTranscription={(text) => {
                              const current = remarkRef.current || "";
                              const isEmpty = !current || current === "<p><br></p>";
                              const next = isEmpty ? `<p>${text}</p>` : `${current}<p>${text}</p>`;
                              remarkRef.current = next;
                              setFieldValue("remark", next);
                              setRemarkCollapsed(false);
                            }}
                          />
                        </span>
                      </div>
                      <div className={remarkCollapsed ? "d-none d-md-block" : ""}><div className="card-body">
                        <div className="row align-items-center">
                          <div className="col">
                            <div className="form-control p-0 overflow-hidden">
                              <ReactQuill
                                value={values.remark}
                                onChange={(text) => {
                                  remarkRef.current = text;
                                  setFieldValue("remark", text);
                                }}
                                className={`border-0 h-200px mb-40px ${
                                  deliveryNote?.remark && "bg-yellow-100 bg-opacity-25"
                                }`}
                              />
                            </div>
                          </div>
                        </div>
                      </div></div>
                    </div>
                    {(isAddMode || isInDraftedState) && (
                      <SignaturePadCard
                        key="emp-sig"
                        title={`${t("customers.menu.signature")} ${t("common.expert")}`}
                        signatureRef={employeeSignatureRef}
                        fieldName="employee_signature_name"
                        lockField={lockEmployeeSignatureField}
                        onToggleLock={handleToggleEmployeeSignatureLock}
                        onClear={() => employeeSignatureRef.current.clear()}
                        disabled={true}
                        defaultImage={isAddMode ? me.signature || null : deliveryNote?.employee_signature || null}
                        defaultCollapsed={!!(isAddMode ? me.signature : deliveryNote?.employee_signature)}
                      />
                    )}
                    {(isAddMode || isInDraftedState) && (
                      <SignaturePadCard
                        key="cust-sig"
                        title={`${t("customers.menu.signature")} ${t("customers.customer")}`}
                        signatureRef={customerSignatureRef}
                        fieldName="customer_signature_name"
                        signatureFieldName="customer_signature"
                        signatureDateFieldName="customer_signature_date"
                        setFieldValue={setFieldValue}
                        lockField={lockCustomerSignatureField}
                        onToggleLock={handleToggleCustomerSignatureLock}
                        onClear={() => {
                          customerSignatureRef.current.clear();
                          setFieldValue("customer_signature", "");
                        }}
                        defaultImage={isAddMode ? null : deliveryNote?.customer_signature || null}
                        defaultCollapsed={isAddMode || !deliveryNote?.customer_signature}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Documentation */}
              <div className={`tab-pane fade${tab === TABS.DOCUMENTATION ? " active show" : ""}`} id="tab-doc">
                <DeliveryNoteDocumentationForm
                  field="documentation"
                  schema={values.documentation_schema}
                  templateValues={template.documentation}
                  values={values.documentation}
                  errors={errors.documentation}
                  setFieldValue={setFieldValue}
                />
              </div>
              <div className={`tab-pane fade${tab === TABS.COWORKERS ? " active show" : ""}`} id="tab-coworkers">
                <div className="card border-0 mb-4">
                  <div className="card-header h6 mb-0 bg-none p-3">
                    <i className="fa fa-user-check fa-lg fa-fw text-dark text-opacity-50 me-1" />{" "}
                    {t("delivery_notes.performed_by")}
                  </div>
                  <div className="card-body">
                    <select
                      className={`form-select${errors.performed_by_id ? " is-invalid" : ""}`}
                      disabled={!canSubmit || !isManager}
                      value={values.performed_by_id == null ? "" : String(values.performed_by_id)}
                      onChange={(e) => {
                        const v = e.target.value;
                        const next = v === "" ? null : Number(v);
                        setFieldValue("performed_by_id", next);
                        if (next != null) {
                          setFieldValue(
                            "coworker_ids",
                            values.coworker_ids.filter((id) => id !== next),
                          );
                        }
                      }}
                    >
                      <option value="">{t("common.choose")}</option>
                      {technicians.map((tech) => (
                        <option key={tech.id} value={String(tech.id)}>
                          {tech.name}
                          {tech.number ? ` (${tech.number})` : ""}
                        </option>
                      ))}
                    </select>
                    {errors.performed_by_id && (
                      <div className="invalid-feedback d-block">{errors.performed_by_id}</div>
                    )}
                  </div>
                </div>
                <div className="card border-0 mb-4">
                  <div className="card-header h6 mb-0 bg-none p-3">
                    <i className="fa fa-users fa-lg fa-fw text-dark text-opacity-50 me-1" />{" "}
                    {t("delivery_notes.coworkers")}
                  </div>
                  <div className="card-body">
                    {(() => {
                      const coworkerOptions = technicians.filter(
                        (u) => u.id !== values.performed_by_id,
                      );
                      return (
                        <select
                          multiple
                          className="form-select"
                          size={Math.min(coworkerOptions.length, 10) || 1}
                          disabled={!canSubmit || coworkerOptions.length === 0}
                          value={values.coworker_ids.map(String)}
                          onChange={(e) => {
                            const selected = Array.from(e.target.selectedOptions, (opt) => Number(opt.value));
                            setFieldValue("coworker_ids", selected);
                          }}
                        >
                          {coworkerOptions.map((tech) => (
                            <option key={tech.id} value={String(tech.id)}>
                              {tech.name}
                              {tech.number ? ` (${tech.number})` : ""}
                            </option>
                          ))}
                        </select>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <div className={`tab-pane fade${tab === TABS.COMMENTS ? " active show" : ""}`} id="tab-com">
                <DeliveryNoteCommentsForm
                  field="comments"
                  values={values.comments}
                  setFieldValue={setFieldValue}
                  canManageComment={(comment) => me?.id === comment.created_by?.id || hasRole(Role.ADMINISTRATOR)}
                />
              </div>

              <div className="row gx-0 d-none d-md-flex">
                <div className="col">
                  <div className="card border-0 mb-4">
                    <div className="card-body">
                      {offlineNotice}
                      {saveErrorAlert}
                      <div className="d-flex gap-2">
                        <div className="text-nowrap btn-group flex-grow-1">
                          {(isInDraftedState || isAddMode) && (
                            <button
                              type="button"
                              onClick={() => {
                                setFieldValue("state", DELIVERY_NOTE_STATE.DRAFTED).then(submitForm);
                              }}
                              className="btn btn-default"
                              disabled={saveDisabled}
                            >
                              <i className="fa fa-save fa-fw me-1" />{t("delivery_notes.menu.draft")}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              if (isInDraftedState || isAddMode) {
                                setShowConfirmFinalize(true);
                              } else {
                                setFieldValue("state", DELIVERY_NOTE_STATE.DELIVERED).then(submitForm);
                              }
                            }}
                            className="btn btn-success"
                            disabled={saveDisabled}
                          >
                            <i className="fa fa-check fa-fw me-1" />
                            {isInDraftedState || isAddMode
                              ? t("delivery_notes.menu.completed")
                              : isInDeliveredState
                                ? t("delivery_notes.menu.change")
                                : t("delivery_notes.menu.draft")}
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            dirty
                              ? setShowConfirmClose(true)
                              : navigate("/delivery_notes")
                          }
                          className="btn btn-danger"
                          title={t("common.close")}
                        >
                          <i className="fa fa-times" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <MobileActionBarContent>
                {offlineNotice}
                {saveErrorAlert}
                <div className="d-flex gap-2">
                  <div className="text-nowrap btn-group flex-grow-1">
                    {(isInDraftedState || isAddMode) && (
                      <button
                        type="button"
                        onClick={() => {
                          setFieldValue("state", DELIVERY_NOTE_STATE.DRAFTED).then(submitForm);
                        }}
                        className="btn btn-default"
                        disabled={saveDisabled}
                      >
                        <i className="fa fa-save fa-fw me-1" />{t("delivery_notes.menu.draft")}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        if (isInDraftedState || isAddMode) {
                          setShowConfirmFinalize(true);
                        } else {
                          setFieldValue("state", DELIVERY_NOTE_STATE.DELIVERED).then(submitForm);
                        }
                      }}
                      className="btn btn-success"
                      disabled={saveDisabled}
                    >
                      <i className="fa fa-check fa-fw me-1" />
                      {isInDraftedState || isAddMode
                        ? t("delivery_notes.menu.completed")
                        : isInDeliveredState
                          ? t("delivery_notes.menu.change")
                          : t("delivery_notes.menu.draft")}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      dirty
                        ? setShowConfirmClose(true)
                        : navigate("/delivery_notes")
                    }
                    className="btn btn-danger"
                    title={t("common.close")}
                  >
                    <i className="fa fa-times" />
                  </button>
                </div>
              </MobileActionBarContent>

              {showConfirmFinalize && (
                <ConfirmDialog
                  title={t("delivery_notes.menu.completed")}
                  message={t("delivery_notes.menu.confirm_completed")}
                  confirmLabel={t("delivery_notes.menu.completed")}
                  onCancel={() => setShowConfirmFinalize(false)}
                  onConfirm={() => {
                    setShowConfirmFinalize(false);
                    setFieldValue("state", DELIVERY_NOTE_STATE.DELIVERED).then(submitForm);
                  }}
                />
              )}

              {showConfirmClose && (
                <ConfirmDialog
                  title={t("common.close")}
                  message={t("common.confirm_unsaved_changes")}
                  confirmLabel={t("common.close")}
                  confirmClassName="btn btn-danger"
                  onCancel={() => setShowConfirmClose(false)}
                  onConfirm={() => {
                    setShowConfirmClose(false);
                    navigate("/delivery_notes");
                  }}
                />
              )}

              {DEV_MODE && (
                <pre
                  className="m-t-xl"
                  style={{
                    fontSize: "1rem",
                    padding: ".25rem .5rem",
                    overflowX: "scroll",
                  }}
                >
                  VALUES: {JSON.stringify(values.customer_property, null, 2)}
                  <br />
                  ERRORS: {JSON.stringify(errors, null, 2)}
                </pre>
              )}
            </div>
          </Form>
          );
        }}
      </Formik>
    </div>
  );
}

export default DeliveryNoteAddEdit;
